import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { changePassword, revokeAllSessions } from '../../services/auth.service';
import { handleLogout } from '../../utils/auth';
import { useModal } from '../../contexts/ModalContext';

const SecuritySection = () => {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const passwordStrength = useMemo(() => {
    const password = passwordForm.new_password;
    if (!password) return { label: 'Faible', color: '#dc3545' };
    if (password.length < 8) return { label: 'Faible', color: '#dc3545' };
    if (password.length < 12) return { label: 'Moyen', color: '#f59e0b' };
    return { label: 'Fort', color: '#28a745' };
  }, [passwordForm.new_password]);

  const resetFeedback = () => {
    setError('');
    setSuccess('');
  };

  const closePasswordModal = (resetMessages = true) => {
    setShowPasswordModal(false);
    setPasswordForm({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    if (resetMessages) {
      resetFeedback();
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    resetFeedback();

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }

    setSavingPassword(true);
    try {
      await changePassword(passwordForm);
      setSuccess('Mot de passe mis à jour. Reconnexion requise.');
      setTimeout(() => {
        handleLogout(navigate);
      }, 1200);
      closePasswordModal(false);
    } catch (err) {
      setError(err.message || 'Impossible de changer le mot de passe.');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleRevokeAll = async () => {
    await openModal({
      title: 'Révoquer toutes les sessions',
      message: 'Toutes les sessions actives seront terminées et vous serez déconnecté. Continuer ?',
      confirmText: 'Révoquer',
      cancelText: 'Annuler',
      type: 'warning',
      icon: 'fa-shield-halved',
      onConfirm: async () => {
        setRevoking(true);
        resetFeedback();

        try {
          await revokeAllSessions();
          setSuccess('Toutes les sessions ont été révoquées. Reconnexion requise.');
          setTimeout(() => {
            handleLogout(navigate);
          }, 1200);
        } catch (err) {
          setError(err.message || 'Impossible de révoquer les sessions.');
        } finally {
          setRevoking(false);
        }
      }
    });
  };

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h2 className="settings-section-title">Sécurité du compte</h2>
        <p className="settings-section-description">
          Gérez votre mot de passe et invalidez les sessions actives si nécessaire
        </p>
      </div>

      {error && (
        <div className="settings-alert error">
          <i className="fa-solid fa-triangle-exclamation"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="settings-alert success">
          <i className="fa-solid fa-circle-check"></i>
          {success}
        </div>
      )}

      <div className="security-card">
        <div className="security-card-icon">
          <i className="fa-solid fa-lock"></i>
        </div>
        <div className="security-card-content">
          <h3 className="security-card-title">Mot de passe</h3>
          <p className="security-card-description">
            Changez votre mot de passe et invalidez immédiatement les jetons existants
          </p>
          <button
            className="security-card-action"
            onClick={() => setShowPasswordModal(true)}
          >
            Modifier le mot de passe
          </button>
        </div>
      </div>

      <div className="security-card">
        <div className="security-card-icon warning">
          <i className="fa-solid fa-right-from-bracket"></i>
        </div>
        <div className="security-card-content">
          <h3 className="security-card-title">Déconnexion globale</h3>
          <p className="security-card-description">
            Révoquez toutes les sessions actives sur vos appareils et reconnectez-vous proprement
          </p>
          <button
            className="security-card-action"
            onClick={handleRevokeAll}
            disabled={revoking}
          >
            {revoking ? 'Révocation...' : 'Déconnecter tous les appareils'}
          </button>
        </div>
      </div>

      <div className="security-card">
        <div className="security-card-icon success">
          <i className="fa-solid fa-shield-halved"></i>
        </div>
        <div className="security-card-content">
          <h3 className="security-card-title">Authentification à deux facteurs</h3>
          <p className="security-card-description">
            La 2FA est prévue pour une phase ultérieure. Pour l’instant, la rotation du mot de passe protège vos sessions.
          </p>
          <span className="security-status disabled">
            <i className="fa-solid fa-circle-minus"></i>
            Bientôt disponible
          </span>
        </div>
      </div>

      {showPasswordModal && (
        <div
          className="telegram-modal-backdrop"
          onClick={closePasswordModal}
          style={{ zIndex: 3000 }}
        >
          <div
            className="telegram-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="telegram-modal-header">
              <div>
                <p className="telegram-modal-kicker">Sécurité</p>
                <h3 className="telegram-modal-title">Changer le mot de passe</h3>
              </div>
              <button
                className="btn-close-popup"
                onClick={closePasswordModal}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <form onSubmit={handlePasswordSubmit} className="settings-form">
              <div className="settings-form-field">
                <label className="settings-form-label">Mot de passe actuel</label>
                <input
                  type="password"
                  className="settings-form-input"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, current_password: e.target.value }))}
                  required
                />
              </div>

              <div className="settings-form-field">
                <label className="settings-form-label">Nouveau mot de passe</label>
                <input
                  type="password"
                  className="settings-form-input"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, new_password: e.target.value }))}
                  required
                />
                {passwordForm.new_password && (
                  <p className="settings-form-hint" style={{ color: passwordStrength.color }}>
                    Force: {passwordStrength.label}
                  </p>
                )}
              </div>

              <div className="settings-form-field">
                <label className="settings-form-label">Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  className="settings-form-input"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirm_password: e.target.value }))}
                  required
                />
                {passwordForm.confirm_password && passwordForm.confirm_password !== passwordForm.new_password && (
                  <p className="settings-form-hint" style={{ color: '#dc3545' }}>
                    Les mots de passe ne correspondent pas
                  </p>
                )}
              </div>

              <div className="settings-form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closePasswordModal}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={savingPassword || passwordForm.new_password !== passwordForm.confirm_password || !passwordForm.new_password}
                >
                  {savingPassword ? 'Sauvegarde...' : 'Changer le mot de passe'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySection;
